import { useParams } from "react-router-dom";
import { useBind, useBindings, useCustomer, useInventory, useProfiles, useUnbind } from "../../api/hooks";
import { EnvironmentGate, useRememberEnvironment } from "../../components/Environment";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, btnSecondary, inputClass } from "../../components/ui";
import { moduleById } from "../../lib/modules";
import { ContextBar, InventoryStateNote } from "./ContextBar";

/**
 * Section 10.2: a binding is what makes a profile reachable - dialled
 * number -> profile - and sync serves only bound profiles. Every number
 * in the instance, from its inventory, with the profile it reaches;
 * nothing is typed.
 */
export function NumbersPage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="numbers" />;
  return <Numbers environmentId={environmentId} />;
}

function Numbers({ environmentId }: { environmentId: string }) {
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const inventory = useInventory(environmentId);
  const bindings = useBindings(environmentId);
  const bind = useBind(environmentId);
  const unbind = useUnbind(environmentId);
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const numbers = (inventory.data?.items ?? []).filter((i) => i.type === "phone_number").map((i) => i.name);
  const bound = new Map((bindings.data?.bindings ?? []).map((b) => [b.phoneNumber, b]));
  // A number bound by hand to one the inventory no longer lists still needs to be visible to unbind.
  const rows = [...new Set([...numbers, ...bound.keys()])].map((phoneNumber) => ({ phoneNumber, binding: bound.get(phoneNumber) }));
  const busy = bind.isPending || unbind.isPending;
  const list = profiles.data?.profiles ?? [];

  return (
    <>
      <PageHeader title="Flow configuration · Numbers" />
      <div className="grid gap-5">
        <ContextBar environmentId={environmentId} environmentName={envName} live={env.data?.customer.live ?? false} onEnvironment={(id) => `/flow-configuration/${id}/numbers`}>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {rows.length} number{rows.length === 1 ? "" : "s"} · {bound.size} bound
            {inventory.data?.observedAt && (
              <>
                {" "}· inventory as of <RelativeTime iso={inventory.data.observedAt} />
              </>
            )}
          </span>
        </ContextBar>
        <InventoryStateNote state={inventory.data?.state} environmentName={envName} />
        {(inventory.isPending || bindings.isPending) && <LoadingState label="Loading numbers" />}
        {bindings.isError && <ErrorState error={bindings.error} onRetry={() => void bindings.refetch()} />}
        {inventory.data && bindings.data && (
          <Card>
            <DataTable
              caption={`Which profile each number in ${envName} reaches`}
              rows={rows}
              rowKey={(r) => r.phoneNumber}
              sortBy={(r) => r.phoneNumber}
              filter={rows.length > 3}
              empty={<EmptyState title="No numbers yet" hint="The instance's phone numbers arrive with its inventory. Claim a number in Amazon Connect and it appears here within about a minute." />}
              columns={[
                { key: "number", header: "Number", render: (r) => <span className="font-medium">{r.phoneNumber}</span> },
                {
                  key: "reaches",
                  header: "Reaches",
                  render: (r) => (
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="sr-only">Profile for {r.phoneNumber}</span>
                      <select
                        className={`${inputClass} mt-0 w-56`}
                        value={r.binding?.profileId ?? ""}
                        disabled={busy}
                        onChange={(e) => (e.target.value ? bind.mutate({ phoneNumber: r.phoneNumber, profileId: e.target.value }) : unbind.mutate({ phoneNumber: r.phoneNumber }))}
                      >
                        <option value="">Not bound</option>
                        {list.map((p) => (
                          <option key={p.profileId} value={p.profileId}>{p.name}</option>
                        ))}
                        {r.binding && !list.some((p) => p.profileId === r.binding!.profileId) && <option value={r.binding.profileId}>{r.binding.profileId}</option>}
                      </select>
                      {!r.binding && <StatusPill status="not bound" tone="warning" />}
                    </label>
                  ),
                },
                { key: "since", header: "Since", render: (r) => (r.binding ? <><RelativeTime iso={r.binding.boundAt} /> by {r.binding.boundBy}</> : "-") },
                {
                  key: "action",
                  header: "",
                  render: (r) =>
                    r.binding ? (
                      <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => unbind.mutate({ phoneNumber: r.phoneNumber })}>
                        Unbind
                      </button>
                    ) : null,
                },
              ]}
            />
            {(bind.isError || unbind.isError) && <ErrorState error={bind.error ?? unbind.error} />}
          </Card>
        )}
      </div>
    </>
  );
}
